import * as React from 'react';
import Panel from '../panel/Panel';
import { useStores } from '../../stores';
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Button, Container, Grid, IconButton, LinearProgress, Paper, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { observer } from 'mobx-react-lite';
import { useTranslation } from "react-i18next";
import { useOpenDialog } from '../../hooks/useOpenDialog';
import Can from '../../components/Can';
import KnowledgeTopicDialog from './dialogs/KnowledgeTopicDialog';
import { KnowledgeTopic } from '../../domain/KnowledgeTopic';
import { useConfirm } from 'material-ui-confirm';
import KnowledgeNoteDialog from './dialogs/KnowledgeNoteDialog';
import { KnowledgeNote } from '../../domain/KnowledgeNote';

const KnowledgePage = observer(() => {
    const { t } = useTranslation();
    const { serverStore, knowledgeStore } = useStores();
    const confirm = useConfirm();
    const { isOpen, openDialog, closeDialog } = useOpenDialog();
    const [selectedTopic, setSelectedTopic] = React.useState<KnowledgeTopic | undefined>();
    const [selectedNote, setSelectedNote] = React.useState<KnowledgeNote | undefined>();

    React.useEffect(() => {
        knowledgeStore.getKnowledgeTopics();
    }, [knowledgeStore]);

    const deleteTopic = (id: number) => {
        confirm({
            title: t("are_you_sure"),
            description: `${t("this_will_permanently_delete")}`,
            confirmationText: t("yes"),
            cancellationText: t("no")
        }).then(() => {
            closeDialog();
            setSelectedTopic(undefined);
            knowledgeStore.deleteKnowledgeTopic(id);
        });
    }

    const deleteNote = (id: number) => {
        confirm({
            title: t("are_you_sure"),
            description: `${t("this_will_permanently_delete")}`,
            confirmationText: t("yes"),
            cancellationText: t("no")
        }).then(() => {
            knowledgeStore.deleteKnowledgeNote(id);
        });
    }

    const openCreateTopicDialog = () => {
        openDialog("manage-knowledge-topic")
    }

    const openUpdateTopicDialog = (topic: KnowledgeTopic) => {
        setSelectedTopic(topic);
        openDialog("manage-knowledge-topic")
    }

    const openCreateNoteDialog = (topic: KnowledgeTopic) => {
        setSelectedTopic(topic);
        openDialog("manage-knowledge-note")
    }

    const openUpdateNoteDialog = (note: KnowledgeNote) => {
        setSelectedNote(note);
        openDialog("manage-knowledge-note")
    }

    const closeTopicDialog = () => {
        setSelectedTopic(undefined);
        closeDialog();
    }

    const closeNoteDialog = () => {
        setSelectedNote(undefined);
        setSelectedTopic(undefined);
        closeDialog();
    }

    const headerItems = [
        <Can key="manage_knowledge_topic_btn" do="manage" on='Knowledge'>
                <Button
                    type="button"
                    color="inherit"
                    onClick={openCreateTopicDialog}>
                    {t("create_knowledge_topic")}
                </Button>
            </Can>
    ];

    return (
        <Panel
            title={t("knowledge")}
            headerItems={headerItems}>

            <Container maxWidth="xl">
                {serverStore.serverRequestLoading && <LinearProgress />}
                <Grid container spacing={2}>
                    {knowledgeStore.knowledgeTopics && knowledgeStore.knowledgeTopics.map(topic => (
                        <Grid key={topic.name} item xs={12} sm={6} md={4} >
                            <Paper sx={{ p: 2, height: '100%' }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <h2>{topic.name}</h2>
                                </div>
                                {topic.notes.map(note => (
                                    <Accordion key={note.id} >
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} >
                                            <Typography sx={{ width: '33%', flexShrink: 0 }} >{note.name}</Typography>
                                            <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{note.description}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography component="pre" sx={{ overflow: 'auto', wordWrap: 'break-word', whiteSpace: 'pre-wrap', border: '1px solid #ccc', padding: '8px' }}>
                                                {note.text}
                                            </Typography>
                                        </AccordionDetails>
                                        <AccordionActions>
                                            <Can do="manage" on='Knowledge'>
                                                <IconButton onClick={() => openUpdateNoteDialog(note)}>
                                                    <EditIcon color="primary" />
                                                </IconButton>
                                                <IconButton onClick={() => deleteNote(note.id)}>
                                                    <DeleteIcon color="error" />
                                                </IconButton>
                                            </Can>
                                        </AccordionActions>
                                    </Accordion>
                                ))}
                                <br />
                                <Can do="manage" on='Knowledge'>
                                    <IconButton onClick={() => openCreateNoteDialog(topic)}>
                                        <AddIcon color="primary" />
                                    </IconButton>
                                    <IconButton onClick={() => openUpdateTopicDialog(topic)}>
                                        <EditIcon color="primary" />
                                    </IconButton>
                                    <IconButton onClick={() => deleteTopic(topic.id)}>
                                        <DeleteIcon color="error" />
                                    </IconButton>
                                </Can>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            <KnowledgeTopicDialog
                isOpen={isOpen("manage-knowledge-topic")}
                onClose={closeTopicDialog}
                onDelete={deleteTopic}
                topic={selectedTopic}
            />

            <KnowledgeNoteDialog
                isOpen={isOpen("manage-knowledge-note")}
                onClose={closeNoteDialog}
                topic={selectedTopic}
                note={selectedNote}
            />
        </Panel >
    );
});

export default KnowledgePage;